class PowerHeroCarousel extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.timer = null;
    this.isInitialized = false;

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handlePointerEnter =
      this.handlePointerEnter.bind(this);
    this.handlePointerLeave =
      this.handlePointerLeave.bind(this);
    this.handleFocusIn =
      this.handleFocusIn.bind(this);
    this.handleFocusOut =
      this.handleFocusOut.bind(this);
    this.handleVisibilityChange =
      this.handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    this.slides = Array.from(
      this.querySelectorAll('[data-slide]')
    );

    this.dots = Array.from(
      this.querySelectorAll('[data-dot]')
    );

    this.prevButton = this.querySelector(
      '[data-prev]'
    );

    this.nextButton = this.querySelector(
      '[data-next]'
    );

    this.autoplay =
      this.dataset.autoplay === 'true';

    this.speed =
      Number(this.dataset.speed) || 5000;

    this.addEventListener(
      'click',
      this.handleClick
    );

    this.addEventListener(
      'keydown',
      this.handleKeydown
    );

    this.addEventListener(
      'pointerenter',
      this.handlePointerEnter
    );

    this.addEventListener(
      'pointerleave',
      this.handlePointerLeave
    );

    this.addEventListener(
      'focusin',
      this.handleFocusIn
    );

    this.addEventListener(
      'focusout',
      this.handleFocusOut
    );

    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );

    if (!this.slides.length) {
      return;
    }

    const activeSlideIndex =
      this.slides.findIndex((slide) =>
        slide.classList.contains('is-active')
      );

    this.currentIndex =
      activeSlideIndex >= 0
        ? activeSlideIndex
        : 0;

    this.updateSlides(false);

    if (this.slides.length > 1) {
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();

    this.removeEventListener(
      'click',
      this.handleClick
    );

    this.removeEventListener(
      'keydown',
      this.handleKeydown
    );

    this.removeEventListener(
      'pointerenter',
      this.handlePointerEnter
    );

    this.removeEventListener(
      'pointerleave',
      this.handlePointerLeave
    );

    this.removeEventListener(
      'focusin',
      this.handleFocusIn
    );

    this.removeEventListener(
      'focusout',
      this.handleFocusOut
    );

    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );

    this.isInitialized = false;
  }

  handleClick(event) {
    const previousButton =
      event.target.closest('[data-prev]');

    if (previousButton) {
      event.preventDefault();
      this.prev(true);
      return;
    }

    const nextButton =
      event.target.closest('[data-next]');

    if (nextButton) {
      event.preventDefault();
      this.next(true);
      return;
    }

    const dot = event.target.closest(
      '[data-dot]'
    );

    if (dot) {
      event.preventDefault();

      this.goTo(
        Number(dot.dataset.dot),
        true
      );
    }
  }

  handleKeydown(event) {
    if (this.slides.length <= 1) {
      return;
    }

    const interactiveElement =
      event.target.closest(
        'a, button, input, textarea, select'
      );

    if (
      interactiveElement &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight'
    ) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev(true);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next(true);
    }
  }

  handlePointerEnter() {
    this.stopAutoplay();
  }

  handlePointerLeave() {
    if (!this.contains(document.activeElement)) {
      this.startAutoplay();
    }
  }

  handleFocusIn() {
    this.stopAutoplay();
  }

  handleFocusOut(event) {
    const nextFocusedElement =
      event.relatedTarget;

    if (
      nextFocusedElement &&
      this.contains(nextFocusedElement)
    ) {
      return;
    }

    this.startAutoplay();
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stopAutoplay();
      this.pauseAllVideos();
      return;
    }

    this.syncVideos();
    this.startAutoplay();
  }

  goTo(index, isManual = false) {
    if (!this.slides.length) {
      return;
    }

    const normalizedIndex =
      (
        index + this.slides.length
      ) % this.slides.length;

    if (
      normalizedIndex === this.currentIndex &&
      this.slides[this.currentIndex]
        .classList.contains('is-active')
    ) {
      if (isManual) {
        this.restartAutoplay();
      }

      return;
    }

    this.currentIndex = normalizedIndex;

    this.updateSlides(true);

    if (isManual) {
      this.restartAutoplay();
    }
  }

  next(isManual = false) {
    this.goTo(
      this.currentIndex + 1,
      isManual
    );
  }

  prev(isManual = false) {
    this.goTo(
      this.currentIndex - 1,
      isManual
    );
  }

  updateSlides(announceChange = true) {
    this.slides.forEach(
      (slide, index) => {
        const isActive =
          index === this.currentIndex;

        slide.classList.toggle(
          'is-active',
          isActive
        );

        slide.setAttribute(
          'aria-hidden',
          String(!isActive)
        );

        if ('inert' in slide) {
          slide.inert = !isActive;
        }
      }
    );

    this.dots.forEach((dot, index) => {
      const isActive =
        index === this.currentIndex;

      dot.classList.toggle(
        'is-active',
        isActive
      );

      dot.setAttribute(
        'aria-selected',
        String(isActive)
      );

      dot.setAttribute(
        'aria-current',
        String(isActive)
      );
    });

    this.syncVideos();

    if (announceChange) {
      this.dispatchEvent(
        new CustomEvent(
          'power-hero:slide-change',
          {
            detail: {
              index: this.currentIndex
            }
          }
        )
      );
    }
  }

  syncVideos() {
    this.slides.forEach(
      (slide, index) => {
        const video = slide.querySelector(
          '.power-hero-carousel__video'
        );

        if (!video) {
          return;
        }

        const isActive =
          index === this.currentIndex;

        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;

        if (isActive && !document.hidden) {
          const playPromise = video.play();

          if (
            playPromise &&
            typeof playPromise.catch ===
              'function'
          ) {
            playPromise.catch(() => {});
          }
        } else {
          video.pause();
        }
      }
    );
  }

  pauseAllVideos() {
    this.slides.forEach((slide) => {
      const video = slide.querySelector(
        '.power-hero-carousel__video'
      );

      video?.pause();
    });
  }

  startAutoplay() {
    if (
      !this.autoplay ||
      this.slides.length <= 1 ||
      document.hidden
    ) {
      return;
    }

    this.stopAutoplay();

    this.timer = window.setInterval(() => {
      this.next(false);
    }, this.speed);
  }

  stopAutoplay() {
    if (!this.timer) {
      return;
    }

    window.clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (
  !customElements.get('power-hero-carousel')
) {
  customElements.define(
    'power-hero-carousel',
    PowerHeroCarousel
  );
}